import type {Fond} from 'src/typings';
import type {ColumnPinningState, ColumnSizingState, VisibilityState} from '@tanstack/svelte-table';
import {exists} from 'shared/utils/custom-utils';
import {CONTROL_COLUMN_TYPES} from 'src/features/record-grid/lib/constants';

const tableSettingsLocalStorageKey = 'recordGridTableSettings';

interface FondTableSettings {
    columnSizing: ColumnSizingState;
    columnPinning: ColumnPinningState;
    columnVisibility: VisibilityState;
}

interface RecordGridTableSettingsHolder {
    fondSettings: Record<number, FondTableSettings>;
}

class TableSettingsStorage {

    public saveFondSettings(fond: Fond, tableSettings: FondTableSettings) {
        const settingsHolder = this.getLocalStorageSettingsHolder();
        settingsHolder.fondSettings[fond.id] = tableSettings;

        this.saveLocalStorageSettingsHolder(settingsHolder);
    }

    public loadFondSettings(fond: Fond): FondTableSettings {
        const settingsHolder = this.getLocalStorageSettingsHolder();
        const fondTableSettings = settingsHolder.fondSettings[fond.id];
        if (!exists(fondTableSettings)) {
            return {
                columnPinning: {
                    left: [CONTROL_COLUMN_TYPES.RECORD_LINK]
                },
                columnSizing: {
                    [CONTROL_COLUMN_TYPES.RECORD_LINK]: 33
                },
                columnVisibility: {}
            }
        }

        return this.addDefaults(fondTableSettings);
    }

    private getLocalStorageSettingsHolder(): RecordGridTableSettingsHolder {
        const jsonString = localStorage.getItem(tableSettingsLocalStorageKey);

        if (!exists(jsonString)) {
            return {fondSettings: {}};
        }

        try {
            return JSON.parse(jsonString) as RecordGridTableSettingsHolder;
        } catch {
            return {fondSettings: {}};
        }
    }

    private saveLocalStorageSettingsHolder(settingsHolder: RecordGridTableSettingsHolder) {
        localStorage.setItem(tableSettingsLocalStorageKey, JSON.stringify(settingsHolder));
    }

    private addDefaults(tableSettings: FondTableSettings): FondTableSettings {
        return {
            columnPinning: {
                left: [CONTROL_COLUMN_TYPES.RECORD_LINK, ...(tableSettings.columnPinning.left ?? []).filter((columnId) => columnId !== CONTROL_COLUMN_TYPES.RECORD_LINK)],
            },
            columnSizing: {
                [CONTROL_COLUMN_TYPES.RECORD_LINK]: 33,
                ...tableSettings.columnSizing
            },
            columnVisibility: {
                ...tableSettings.columnVisibility
            }
        };
    }
}

export const recordGridTableSettingsStorage = new TableSettingsStorage();